import Cookies from "js-cookie";
import { getAuthHeaders, getJwt } from "../../../utils/authHeaders";
import { normalizeBullionMatchQueryParam } from "./bullionMatchLandingLobby";
import { buildBullionRoomFromPlayBootstrap } from "./roomUtils";
import type { BullionLobbyUserActiveGame, BullionRoom } from "./types";

/** Align with lobby / match-landing caches — avoid refetch on every in-app back navigation. */
const BULLION_MY_GAMES_BY_MATCH_CACHE_TTL_MS = 60_000;

/** Per-play row kept for match-landing orphan recovery (fixture not in Trader lobby list). */
export type BullionMyGamesOrphanRow = {
    matchId: string;
    gameId: number;
    gameName: string | null;
    entryStake: number;
    gameStatus: string | null;
    settled: boolean;
    matchName: string | null;
    competition: string | null;
    startTime: string | null;
};

type CacheEntry = {
    authKey: string;
    expiresAt: number;
    byMatch: Record<string, BullionLobbyUserActiveGame[]>;
    orphanRowsByMatchId: Record<string, BullionMyGamesOrphanRow[]>;
};

let cache: CacheEntry | null = null;
let inflight: Promise<Record<string, BullionLobbyUserActiveGame[]>> | null = null;

function authKey(): string | null {
    const st = Cookies.get("c8_session_token") || "";
    const jwt = getJwt() || "";
    if (!st && !jwt) return null;
    return `${st}|${jwt}`;
}

/** Same as `BullionMatchLanding` / the Bullion route — `jwtOptionalAuth` may need `session_token` when JWT is absent. */
export function bullionMyGamesListUrl(tab: "ACTIVE" | "HISTORY" | "FINISHED"): string {
    const params = new URLSearchParams({ tab });
    const st = Cookies.get("c8_session_token");
    if (st) params.set("session_token", st);
    return `${process.env.API_URL}/bullion/my-games?${params.toString()}`;
}

function rowToLobbyGame(row: {
    matchId?: unknown;
    gameId?: unknown;
    gameName?: string | null;
    entryStake?: unknown;
    gameStatus?: string | null;
}): { mid: string; game: BullionLobbyUserActiveGame } | null {
    const mid = String(row.matchId || "").trim();
    if (!mid) return null;
    const gameId = Number(row.gameId);
    if (!Number.isFinite(gameId)) return null;
    return {
        mid,
        game: {
            gameId,
            name: row.gameName ?? null,
            entryStake: Number(row.entryStake ?? 0),
            gameStatus: row.gameStatus ?? null,
        },
    };
}

function mergeRowsIntoByMatch(
    byMatch: Record<string, BullionLobbyUserActiveGame[]>,
    orphanRowsByMatchId: Record<string, BullionMyGamesOrphanRow[]>,
    rows: unknown[]
) {
    for (const row of rows) {
        const mapped = rowToLobbyGame(row as never);
        if (!mapped) continue;
        const { mid, game } = mapped;
        if (!byMatch[mid]) byMatch[mid] = [];
        const list = byMatch[mid];
        if (!list.some((g) => Number(g.gameId) === Number(game.gameId))) {
            list.push(game);
        }

        const r = row as {
            gameName?: string | null;
            settled?: boolean;
            matchName?: string | null;
            competition?: string | null;
            startTime?: string | null;
        };
        if (!orphanRowsByMatchId[mid]) orphanRowsByMatchId[mid] = [];
        const orphanList = orphanRowsByMatchId[mid];
        if (!orphanList.some((g) => Number(g.gameId) === Number(game.gameId))) {
            orphanList.push({
                matchId: mid,
                gameId: game.gameId,
                gameName: r.gameName ?? game.name ?? null,
                entryStake: game.entryStake,
                gameStatus: game.gameStatus,
                settled: Boolean(r.settled),
                matchName: r.matchName ?? null,
                competition: r.competition ?? null,
                startTime: r.startTime != null ? String(r.startTime) : null,
            });
        }
    }
}

function findOrphanRowsForMatchQuery(
    orphanRowsByMatchId: Record<string, BullionMyGamesOrphanRow[]>,
    matchQuery: string
): BullionMyGamesOrphanRow[] {
    const want = normalizeBullionMatchQueryParam(matchQuery);
    if (!want) return [];
    for (const [mid, rows] of Object.entries(orphanRowsByMatchId)) {
        if (normalizeBullionMatchQueryParam(mid) === want) return rows;
    }
    return [];
}

/** Cached my-games rows for a fixture URL when the lobby list no longer includes that match. */
export function peekBullionMyGamesOrphanRowsForMatch(matchQuery: string): BullionMyGamesOrphanRow[] | null {
    const key = authKey();
    if (key == null) return null;
    if (!cache || cache.authKey !== key || cache.expiresAt <= Date.now()) return null;
    return findOrphanRowsForMatchQuery(cache.orphanRowsByMatchId, matchQuery);
}

/** Build synthetic landing room + entered contests from merged `/bullion/my-games` rows. */
export function buildBullionOrphanLandingFromMyGamesRows(
    rows: BullionMyGamesOrphanRow[]
): { room: BullionRoom; plays: BullionLobbyUserActiveGame[] } | null {
    if (rows.length === 0) return null;
    const best =
        rows.find((r) => Boolean(r.matchName && String(r.matchName).trim())) || rows[0];
    const matchId = String(best.matchId || rows[0]?.matchId || "").trim();
    if (!matchId) return null;
    let syn = buildBullionRoomFromPlayBootstrap({
        matchId,
        matchName: best.matchName ?? null,
        competition: best.competition ?? null,
        startsAt: best.startTime ?? null,
    });
    const allSettled = rows.every((r) => Boolean(r.settled));
    const startMs = best.startTime ? new Date(String(best.startTime)).getTime() : Number.NaN;
    const kickoffPassedMeta = Number.isFinite(startMs) && startMs <= Date.now();
    /** Settled plays alone do not mean the fixture is finished — upcoming matches stay open for new entry. */
    if (allSettled && kickoffPassedMeta) {
        syn = {
            ...syn,
            fixturePhase: "finished",
            isLive: false,
            status: "MATCH COMPLETE",
        };
    } else if (kickoffPassedMeta) {
        syn = {
            ...syn,
            fixturePhase: "live",
            isLive: true,
        };
    }
    const plays: BullionLobbyUserActiveGame[] = rows.map((row) => ({
        gameId: row.gameId,
        name: row.gameName ?? null,
        entryStake: row.entryStake,
        gameStatus: row.gameStatus,
    }));
    return { room: syn, plays };
}

export function invalidateBullionMyGamesByMatchIdCache(): void {
    cache = null;
}

/** Non-expired merged my-games map (for instant match-landing paint after leaving in-play). */
export function peekBullionMyGamesByMatchIdCache(): Record<string, BullionLobbyUserActiveGame[]> | null {
    const key = authKey();
    if (key == null) return null;
    if (!cache || cache.authKey !== key || cache.expiresAt <= Date.now()) return null;
    return cache.byMatch;
}

/**
 * Merged ACTIVE + HISTORY `/bullion/my-games` rows keyed by `matchId` — deduped in-flight + short TTL so
 * the Bullion route and `BullionMatchLanding` do not double-fetch the same lists on mount.
 */
export async function fetchBullionMyGamesByMatchIdCached(opts?: {
    bypassCache?: boolean;
}): Promise<Record<string, BullionLobbyUserActiveGame[]>> {
    const key = authKey();
    if (key == null) return {};

    const now = Date.now();
    if (!opts?.bypassCache && cache && cache.authKey === key && cache.expiresAt > now) {
        return cache.byMatch;
    }

    if (inflight) return inflight;

    inflight = (async () => {
        try {
            /** HISTORY has no tab filter (includes settled); ACTIVE covers open plays — FINISHED tab omitted to save a round-trip. */
            const [activeRes, historyRes] = await Promise.all([
                fetch(bullionMyGamesListUrl("ACTIVE"), { method: "GET", headers: getAuthHeaders() }),
                fetch(bullionMyGamesListUrl("HISTORY"), { method: "GET", headers: getAuthHeaders() }),
            ]);
            const byMatch: Record<string, BullionLobbyUserActiveGame[]> = {};
            const orphanRowsByMatchId: Record<string, BullionMyGamesOrphanRow[]> = {};
            if (activeRes.ok) {
                const data = await activeRes.json().catch(() => ({}));
                const rows = Array.isArray(data?.games) ? data.games : [];
                mergeRowsIntoByMatch(byMatch, orphanRowsByMatchId, rows);
            }
            if (historyRes.ok) {
                const data = await historyRes.json().catch(() => ({}));
                const rows = Array.isArray(data?.games) ? data.games : [];
                mergeRowsIntoByMatch(byMatch, orphanRowsByMatchId, rows);
            }
            if (!opts?.bypassCache) {
                cache = {
                    authKey: key,
                    expiresAt: Date.now() + BULLION_MY_GAMES_BY_MATCH_CACHE_TTL_MS,
                    byMatch,
                    orphanRowsByMatchId,
                };
            }
            return byMatch;
        } finally {
            inflight = null;
        }
    })();

    return inflight;
}
